import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fastapiService } from '../../services/fastapiService';
import { ADMIN_BASE } from '../../config/adminLayers';

type NotificationLog = {
  id: string;
  status?: string;
  channel?: string;
  event_type?: string;
  recipient_email?: string;
  trigger_source?: string;
  provider_message_id?: string;
  error_message?: string;
  created_at?: string;
};

const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentUtcYear = new Date().getUTCFullYear();
const YEAR_OPTIONS = Array.from({ length: currentUtcYear - 2022 }, (_, i) => String(2023 + i));
const MAX_BULK_PDFS = 200;

export const InvoicesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceIdFromUrl = (searchParams.get('workspace_id') || '').trim();
  const tdsFollowupBase = `${ADMIN_BASE}/tds-followup`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');
  const [yearFilter, setYearFilter] = useState<string>(() => searchParams.get('year') || '');
  const [monthFilter, setMonthFilter] = useState<string>(() => searchParams.get('month') || '');
  const [dateField, setDateField] = useState<string>(() =>
    (searchParams.get('date_field') || '').toLowerCase() === 'paid_at' ? 'paid_at' : 'created_at'
  );
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [resendLoadingId, setResendLoadingId] = useState<string | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');
  const [logModalInvoice, setLogModalInvoice] = useState<any | null>(null);
  const [notificationLogs, setNotificationLogs] = useState<NotificationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const buildListParams = (overrides: Record<string, unknown> = {}) => {
    const params: Record<string, unknown> = { page, limit, ...overrides };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (workspaceIdFromUrl) params.workspace_id = workspaceIdFromUrl;
    if (yearFilter) params.year = Number(yearFilter);
    if (monthFilter) params.month = Number(monthFilter);
    if ((yearFilter || monthFilter) && dateField) params.date_field = dateField;
    return params;
  };

  useEffect(() => {
    setPage(1);
  }, [workspaceIdFromUrl, statusFilter, yearFilter, monthFilter, dateField]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (workspaceIdFromUrl) next.set('workspace_id', workspaceIdFromUrl);
    if (statusFilter) next.set('status', statusFilter);
    if (yearFilter) next.set('year', yearFilter);
    if (monthFilter) next.set('month', monthFilter);
    if ((yearFilter || monthFilter) && dateField === 'paid_at') next.set('date_field', 'paid_at');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [workspaceIdFromUrl, statusFilter, yearFilter, monthFilter, dateField, searchParams, setSearchParams]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fastapiService.getInvoices(buildListParams());
        setInvoices(res.data || []);
        setTotal(res.pagination?.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [page, limit, search, statusFilter, yearFilter, monthFilter, dateField, workspaceIdFromUrl]);

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const openInvoicePdf = async (invoiceId: string) => {
    try {
      setPdfLoadingId(invoiceId);
      await fastapiService.regenerateAdminInvoicePdf(invoiceId);
      await fastapiService.openAdminInvoicePdfInNewTab(invoiceId);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to open invoice PDF');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const resendInvoiceEmail = async (invoiceId: string, customerEmail?: string) => {
    const ok = window.confirm(
      customerEmail
        ? `Resend invoice email with PDF to ${customerEmail}?`
        : 'Resend invoice email with PDF to the customer on file?',
    );
    if (!ok) return;
    try {
      setResendLoadingId(invoiceId);
      setError('');
      const res = await fastapiService.resendAdminInvoiceEmail(invoiceId);
      if (!(res as any)?.success && (res as any)?.error) {
        setError(String((res as any).error));
        return;
      }
      const msg = (res as any)?.data?.message || 'Invoice email sent.';
      window.alert(msg);
      const refreshed = await fastapiService.getInvoices(buildListParams());
      setInvoices(refreshed.data || []);
      setTotal(refreshed.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to resend invoice email');
    } finally {
      setResendLoadingId(null);
    }
  };

  const openNotificationLog = async (inv: any) => {
    setLogModalInvoice(inv);
    setNotificationLogs([]);
    setLogsLoading(true);
    try {
      const res = await fastapiService.getInvoiceNotificationLogs(inv.id);
      const rows = Array.isArray(res) ? res : (res as any)?.data || [];
      setNotificationLogs(rows);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to load notification log');
      setLogModalInvoice(null);
    } finally {
      setLogsLoading(false);
    }
  };

  const clearPeriodAndStatusFilters = () => {
    setPage(1);
    setStatusFilter('');
    setYearFilter('');
    setMonthFilter('');
    setDateField('created_at');
  };

  const downloadFilteredPdfs = async () => {
    if (bulkDownloading) return;
    setBulkDownloading(true);
    setBulkProgress('Loading matching invoices…');
    setError('');
    try {
      const collected: any[] = [];
      let nextPage = 1;
      let totalCount = 0;
      const pageSize = 100;
      do {
        const res = await fastapiService.getInvoices(
          buildListParams({ page: nextPage, limit: pageSize })
        );
        collected.push(...(res.data || []));
        totalCount = res.pagination?.total || collected.length;
        nextPage += 1;
      } while (collected.length < totalCount && collected.length < MAX_BULK_PDFS && nextPage <= 20);

      const toDownload = collected.slice(0, MAX_BULK_PDFS);
      if (toDownload.length === 0) {
        setError('No invoices match the current filters');
        return;
      }
      for (let i = 0; i < toDownload.length; i += 1) {
        setBulkProgress(`Downloading ${i + 1} of ${toDownload.length}…`);
        await fastapiService.downloadAdminInvoicePdf(toDownload[i].id);
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
      if (totalCount > toDownload.length) {
        window.alert(
          `Downloaded ${toDownload.length} of ${totalCount} invoices. Narrow the month or status filters to download the rest.`
        );
      }
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to download invoice PDFs');
    } finally {
      setBulkDownloading(false);
      setBulkProgress('');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-admin-100 text-admin-700';
    }
  };

  const getNotifStatusColor = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'sent':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'skipped':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-admin-100 text-admin-700';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Invoices</h1>
        <p className="mt-1 text-sm text-admin-600">
          View and manage all invoices across the system.{' '}
          <Link to={tdsFollowupBase} className="text-sky-700 hover:text-sky-900 font-medium">
            TDS / 26AS follow-up
          </Link>
        </p>
        {workspaceIdFromUrl && (
          <p className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Filtered by workspace ID{' '}
            <span className="font-mono text-xs">{workspaceIdFromUrl}</span> — clear the URL query or open Invoices from the main menu to see all.
          </p>
        )}
      </div>
      
      <div className="admin-card p-6">
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search by invoice number or email..."
              className="admin-input flex-1 min-w-[220px] max-w-md"
            />
            <label className="flex flex-col gap-1 text-xs text-admin-600">
              Status
              <select
                value={statusFilter}
                onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                className="admin-input w-40 text-sm"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-admin-600">
              Month
              <select
                value={monthFilter}
                onChange={(e) => { setPage(1); setMonthFilter(e.target.value); }}
                className="admin-input w-40 text-sm"
              >
                <option value="">All months</option>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-admin-600">
              Year
              <select
                value={yearFilter}
                onChange={(e) => { setPage(1); setYearFilter(e.target.value); }}
                className="admin-input w-28 text-sm"
              >
                <option value="">All years</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-admin-600">
              Date field
              <select
                value={dateField}
                onChange={(e) => { setPage(1); setDateField(e.target.value); }}
                className="admin-input w-44 text-sm"
                disabled={!yearFilter && !monthFilter}
              >
                <option value="created_at">Invoice date</option>
                <option value="paid_at">Payment date</option>
              </select>
            </label>
            <button
              type="button"
              className="admin-button-secondary"
              onClick={clearPeriodAndStatusFilters}
              disabled={!statusFilter && !yearFilter && !monthFilter && dateField === 'created_at'}
            >
              Clear filters
            </button>
            <button
              type="button"
              className="admin-button-primary"
              onClick={downloadFilteredPdfs}
              disabled={bulkDownloading || loading || total === 0}
            >
              {bulkDownloading ? (bulkProgress || 'Downloading…') : `Download PDFs${total ? ` (${total})` : ''}`}
            </button>
          </div>
          {(yearFilter || monthFilter) && (
            <p className="text-xs text-admin-500">
              Showing invoices whose {dateField === 'paid_at' ? 'payment date' : 'invoice date'} falls in{' '}
              {monthFilter ? `${MONTH_OPTIONS.find((m) => m.value === monthFilter)?.label || monthFilter} ` : ''}
              {yearFilter || currentUtcYear}
              {!yearFilter && monthFilter ? ' (current year)' : ''}
              {statusFilter ? ` · status ${statusFilter}` : ''}.
            </p>
          )}
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-600">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Workspace</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Email notification</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {loading ? (
                <tr><td className="px-4 py-4 text-admin-500" colSpan={8}>Loading...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td className="px-4 py-4 text-admin-500" colSpan={8}>No invoices found</td></tr>
              ) : (
                invoices.map((inv: any) => {
                  const last = inv.last_notification as NotificationLog | undefined;
                  return (
                    <tr key={inv.id} className="hover:bg-admin-50">
                      <td className="px-4 py-4 text-sm font-mono text-admin-900">
                        {inv.invoice_number || `INV-${inv.id.substring(0, 8).toUpperCase()}`}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-900">
                        <div>{inv.user_profiles?.email || '-'}</div>
                        {inv.user_profiles?.full_name && (
                          <div className="text-xs text-admin-500">{inv.user_profiles.full_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        {inv.workspaces?.name || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-admin-900">
                        {formatCurrency(parseFloat(inv.amount || inv.total_amount || 0), inv.currency || 'INR')}
                        {inv.tds_applicable && (
                          <div className="mt-1 text-xs text-amber-700">
                            TDS {formatCurrency(parseFloat(inv.tds_amount || 0), inv.currency || 'INR')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                          {inv.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        {last ? (
                          <button
                            type="button"
                            className="text-left hover:underline"
                            onClick={() => openNotificationLog(inv)}
                            title="View full notification log"
                          >
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getNotifStatusColor(last.status)}`}>
                              {last.status || 'unknown'}
                            </span>
                            <div className="mt-1 text-xs text-admin-500">
                              {formatDateTime(last.created_at)}
                              {inv.notification_count > 1 ? ` · ${inv.notification_count} sends` : ''}
                            </div>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-xs text-admin-400 hover:text-admin-600 hover:underline"
                            onClick={() => openNotificationLog(inv)}
                          >
                            No sends logged
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-500">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button
                            type="button"
                            className="text-sky-700 hover:text-sky-900 font-medium disabled:opacity-50"
                            disabled={pdfLoadingId === inv.id}
                            onClick={() => openInvoicePdf(inv.id)}
                          >
                            {pdfLoadingId === inv.id ? 'Opening…' : 'View PDF'}
                          </button>
                          <button
                            type="button"
                            className="text-admin-700 hover:text-admin-900 font-medium disabled:opacity-50"
                            disabled={resendLoadingId === inv.id}
                            onClick={() => resendInvoiceEmail(inv.id, inv.user_profiles?.email)}
                          >
                            {resendLoadingId === inv.id ? 'Sending…' : 'Resend email'}
                          </button>
                          <button
                            type="button"
                            className="text-admin-600 hover:text-admin-900 font-medium"
                            onClick={() => openNotificationLog(inv)}
                          >
                            Email log
                          </button>
                          {inv.tds_applicable && (
                            <Link
                              to={`${tdsFollowupBase}?invoice_id=${encodeURIComponent(inv.id)}`}
                              className="text-amber-700 hover:text-amber-900 font-medium"
                            >
                              TDS / 26AS
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-admin-600">
            Page {page} of {Math.ceil(total / limit)} · {total} total invoices
          </div>
          <div className="flex items-center gap-2">
            <button 
              className="admin-button" 
              disabled={page <= 1} 
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button 
              className="admin-button" 
              disabled={page >= Math.ceil(total / limit)} 
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {logModalInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-admin-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-admin-900">Email notification log</h2>
                <p className="text-sm text-admin-500 font-mono">
                  {logModalInvoice.invoice_number || `INV-${String(logModalInvoice.id).slice(0, 8).toUpperCase()}`}
                </p>
              </div>
              <button
                type="button"
                className="text-admin-500 hover:text-admin-800"
                onClick={() => setLogModalInvoice(null)}
              >
                Close
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {logsLoading ? (
                <p className="text-sm text-admin-500">Loading…</p>
              ) : notificationLogs.length === 0 ? (
                <p className="text-sm text-admin-500">No notification sends recorded for this invoice yet.</p>
              ) : (
                <ul className="space-y-3">
                  {notificationLogs.map((log) => (
                    <li key={log.id} className="rounded-md border border-admin-200 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getNotifStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                        <span className="text-xs text-admin-500">{formatDateTime(log.created_at)}</span>
                        {log.trigger_source && (
                          <span className="text-xs text-admin-600 bg-admin-50 px-2 py-0.5 rounded">
                            {log.trigger_source}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-admin-800">
                        To: {log.recipient_email || '—'}
                      </div>
                      {log.provider_message_id && (
                        <div className="mt-1 text-xs text-admin-500 font-mono">
                          Provider ID: {log.provider_message_id}
                        </div>
                      )}
                      {log.error_message && (
                        <div className="mt-1 text-xs text-red-600">{log.error_message}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
